import React from 'react';
import mixpanel from 'mixpanel-browser';
import moment from 'moment';
import { PETPLACE_SMS_LIST } from '../../../utilities';
import { PetParent, PetParentAnimal } from '../../../types/PetParent';

// Helper function to check if pet parent has medical records
const hasMedicalRecords = (petParent: PetParent | null | undefined): boolean => {
  if (!petParent?.animals) return false;
  if (petParent.awo?.name === 'Happy Bark') return false;
  return petParent.animals.some((animal) => animal.animalMedias?.some((media) => media.fileType === 'pdf'
    && (media.url.includes('pdfmonkey') || media.url.includes('shelterbuddy-records'))));
};

// Helper functions needed by the get* functions
const calculatePetAgeInMonths = (animal: PetParentAnimal): number => {
  const birthMoment = animal?.birthDate ? moment(animal.birthDate) : null;
  if (!birthMoment || !birthMoment.isValid()) {
    // default to 1 year old
    return 12;
  }
  return moment().diff(birthMoment, 'months');
};

const isPetYoung = (animal: PetParentAnimal): boolean => calculatePetAgeInMonths(animal) <= 6;

export const getPrimaryAnimal = (petParent: PetParent | null | undefined): PetParentAnimal | null => {
  if (!petParent?.animals || petParent.animals.length === 0) {
    return null;
  }
  if (petParent.primaryAnimalId != null) {
    const primary = petParent.animals.find(
      (a) => String(a.id) === String(petParent.primaryAnimalId),
    );
    if (primary) return primary;
  }
  return petParent.animals[0];
};

export const getDogBehavorialLessons = (feedbackIssues?: string[], dogBehavorialLessons?: any[]) => {
  if (!feedbackIssues || !dogBehavorialLessons) {
    return [];
  }

  // Filter for matching lessons and use a Map to ensure uniqueness by title
  const uniqueLessons = new Map();
  dogBehavorialLessons
    .filter((lesson) => feedbackIssues.includes(lesson.relatedIssue))
    .forEach((lesson) => uniqueLessons.set(lesson.title, lesson));

  return Array.from(uniqueLessons.values());
};

export const getCatBehavorialLessons = (feedbackIssues?: string[], catBehavorialLessons?: any[]) => {
  if (!feedbackIssues || !catBehavorialLessons) {
    return [];
  }
  // Filter for matching lessons and use a Map to ensure uniqueness by title
  const uniqueLessons = new Map();
  catBehavorialLessons
    .filter((lesson) => feedbackIssues.includes(lesson.relatedIssue))
    .forEach((lesson) => uniqueLessons.set(lesson.title, lesson));

  return Array.from(uniqueLessons.values());
};

// Daily-routine lessons now live in the hub, so these open in place rather than
// in a new tab. The slug per pet type comes from src/content/lessons/catalog.json.
const ROUTINE_SLUG: Record<string, string> = {
  dog: 'your-dogs-daily-routine',
  puppy: 'your-puppys-daily-routine',
  cat: 'your-cats-daily-routine',
  kitten: 'your-kittens-daily-routine',
};

export const getDogRoutineCard = (
  petParent: PetParent | null | undefined,
  navigate: (to: string) => void,
) => {
  if (!petParent) return null;

  const primaryAnimal = getPrimaryAnimal(petParent);
  if (!primaryAnimal || primaryAnimal.animalType.toLowerCase() !== 'dog') return null;

  const isYoung = isPetYoung(primaryAnimal);
  const petType = isYoung ? 'puppy' : 'dog';
  const routineUrl = `/hub/lessons-and-guides/${ROUTINE_SLUG[petType]}`;

  return {
    title: `What's the best daily routine for your ${petType}?`,
    subtitle: `Simple tips to keep your ${petType} happy and healthy every day.`,
    background: '#DDE7F3',
    buttonText: 'See The Routine',
    buttonColor: '#000',
    image: '/hub/hub_dog_routine_cta.png',
    imageWidth: 138,
    onClick: () => {
      mixpanel.track('recommended_routine_click', {
        pet_type: petType,
        link: routineUrl,
      }, () => {
        navigate(routineUrl);
      });
    },
  };
};

export const getCatRoutineCard = (
  petParent: PetParent | null | undefined,
  navigate: (to: string) => void,
) => {
  if (!petParent) return null;

  const primaryAnimal = getPrimaryAnimal(petParent);
  if (!primaryAnimal) return null;

  const animalType = primaryAnimal.animalType.toLowerCase();
  if (animalType !== 'cat' && animalType !== 'kitten') return null;

  const isYoung = isPetYoung(primaryAnimal);
  const petType = (animalType === 'kitten' || isYoung) ? 'kitten' : 'cat';
  const routineUrl = `/hub/lessons-and-guides/${ROUTINE_SLUG[petType]}`;

  return {
    title: `What's the best daily routine for your ${petType}?`,
    subtitle: `Simple tips to keep your ${petType} happy and healthy every day.`,
    background: '#DDE7F3',
    buttonText: 'See The Routine',
    buttonColor: '#000',
    image: '/hub/hub_cat_routine_cta.png',
    imageWidth: 138,
    onClick: () => {
      mixpanel.track('recommended_routine_click', {
        pet_type: petType,
        link: routineUrl,
      }, () => {
        navigate(routineUrl);
      });
    },
  };
};

export const getCtaCards = ({
  petParent, navigate, isHideRecordsCtaCard, country,
}: {
  petParent: PetParent | null | undefined;
  navigate: (to: string) => void;
  isHideRecordsCtaCard: boolean;
  country?: string;
}) => {
  const isNorthAm = new Set(['USA', 'Canada']).has(country || '');
  const routineCard = getDogRoutineCard(petParent, navigate) ?? getCatRoutineCard(petParent, navigate);

  const membershipCard = {
    title: "Don't let your pet's progress stop here",
    subtitle: 'Keep unlimited trainer support for your pet — extend before your 30 days end.',
    background: '#F2E7FE',
    buttonText: 'Extend Membership',
    buttonColor: '#000',
    image: '/hub/hub_subscription_cta.png',
    imageWidth: 136,
    isHidden: petParent?.awo?.membershipType !== 'Plus',
    onClick: () => {
      mixpanel.track('extend_membership_click', { from_where: 'home' }, () => {
        window.open('https://petcademy.org/pricing/?utm_source=umso&utm_medium=feature', '_blank');
      });
    },
  };

  const routineCardWithHidden = {
    ...routineCard,
    isHidden: !(routineCard && petParent?.awo?.membershipType && ['Plus', 'Premium'].includes(petParent.awo.membershipType)),
  };

  const recordsCard = {
    title: "Your pet's records are ready to view",
    subtitle: "Access your pet's medical records, adoption packet and microchip number",
    subtitleColor: 'rgba(255,255,255,0.7)',
    background: '#1E1E1E',
    color: 'white',
    buttonColor: 'white',
    buttonText: 'View Records',
    image: '/hub/hub_records_cta.png',
    isHidden: isHideRecordsCtaCard || !hasMedicalRecords(petParent),
    onClick: () => {
      localStorage.setItem('isHideRecordsCtaCard', 'isHidden');
      mixpanel.track('view_records_click', undefined, () => navigate('/hub/my-pets'));
    },
  };

  const lpmCard = {
    title: 'Protect your pet for life',
    subtitle: 'Personalized 24/7 Lost Pet Recovery + Save $30 on Rover and $25 at Petco',
    background: '#fff',
    buttonText: 'Get Started Today',
    buttonColor: '#000',
    image: '/hub/hub_lpm_cta.png',
    imageWidth: 136,
    isHidden: !isNorthAm,
    onClick: () => {
      mixpanel.track('petplace_offer_click', undefined, () => {
        window.open('https://www.petplace.com/lost-pet-protection?utm_source=Petcademy&utm_medium=referral&utm_campaign=Adopterhub', '_blank');
      });
    },
  };

  const DOG_GUIDE_URL = '/hub/lessons-and-guides/welcoming-your-adopted-dog-home'; // Welcoming Your Adopted Dog Home
  const CAT_GUIDE_URL = '/hub/lessons-and-guides/welcoming-your-adopted-cat-home'; // Welcoming Your Adopted Cat Home

  const thinkificCard = {
    title: 'Set you and your pet up for success',
    subtitle: 'A practical guide to help your new pet settle in, build trust, and establish healthy routines.',
    background: '#F1F8E9',
    buttonText: 'Open Guide',
    buttonColor: '#000',
    image: '/hub/thinkific_cta.png',
    onClick: () => {
      const primaryAnimal = getPrimaryAnimal(petParent);
      const isDog = primaryAnimal?.animalType?.toLowerCase() === 'dog';
      const guideUrl = isDog ? DOG_GUIDE_URL : CAT_GUIDE_URL;
      mixpanel.track('welcoming_guide_click', { pet_type: isDog ? 'dog' : 'cat' }, () => {
        window.open(guideUrl, '_blank');
      });
    },
  };

  const shopCard = {
    title: 'Turn your pet into a legend',
    subtitle: 'Upload a photo and we\'ll turn your pet into a vintage tee.',
    background: '#F2E7FE',
    buttonText: 'Create Your Legend',
    buttonColor: '#000',
    imageWidth: 136,
    image: '/hub/hub_shop_cta.png',
    onClick: () => {
      mixpanel.track('go_to_store_click', {
        link: `https://shop.petcademy.org/?awoName=${encodeURIComponent(petParent?.awo?.displayName ?? petParent?.awo?.name ?? '')}`,
      }, () => {
        window.open(`https://shop.petcademy.org/?awoName=${encodeURIComponent(petParent?.awo?.displayName ?? petParent?.awo?.name ?? '')}`, '_blank');
      });
    },
  };

  const baseCards = [membershipCard, routineCardWithHidden, recordsCard];
  const tail = [shopCard];
  if (isNorthAm) {
    tail.push(lpmCard);
  }

  return [thinkificCard, ...baseCards, ...tail];
};

export interface QuickActionCardConfig {
  title: string;
  subtitle: string;
  subtitleColor?: string;
  background?: string;
  color?: string;
  buttonColor?: string;
  buttonText: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  onClick: () => void;
}

// Uniform artwork height so every Quick Action card shows its image at the same
// size regardless of the source image's aspect ratio.
const QUICK_ACTION_IMAGE_HEIGHT = 90;

/**
 * The 30-Day Checklist homepage Quick Actions (full design handoff set). The
 * records card only renders when the animal actually has records to show; the
 * whole section is hidden when membership is expired.
 */
export const getQuickActionCards = ({ petParent, navigate }: {
  petParent: PetParent | null | undefined;
  navigate: (to: string) => void;
}): QuickActionCardConfig[] => {
  const shopUrl = `https://shop.petcademy.org/?awoName=${encodeURIComponent(petParent?.awo?.displayName ?? petParent?.awo?.name ?? '')}`;
  const membershipCard: QuickActionCardConfig = {
    title: "Don't let your pet's progress stop here.",
    subtitle: 'Keep unlimited trainer support for your pet — extend before your 30 days end.',
    background: '#F2E7FE',
    buttonText: 'Extend Membership',
    buttonColor: '#3700b3',
    image: '/hub/hub_subscription_cta.png',
    imageHeight: QUICK_ACTION_IMAGE_HEIGHT,
    onClick: () => {
      mixpanel.track('extend_membership_click', { from_where: 'home' }, () => {
        window.open('https://petcademy.org/pricing/?utm_source=umso&utm_medium=feature', '_blank');
      });
    },
  };
  const recordsCard: QuickActionCardConfig = {
    title: "Your pet's records are ready to view",
    subtitle: "Access your pet's medical records, adoption packet and microchip number",
    subtitleColor: 'rgba(255, 255, 255, 0.72)',
    background: '#1c1c1c',
    color: '#fff',
    buttonColor: '#fff',
    buttonText: 'View Records',
    image: '/hub/hub_records_cta.png',
    imageHeight: QUICK_ACTION_IMAGE_HEIGHT,
    onClick: () => {
      mixpanel.track('view_records_click', undefined, () => navigate('/hub/my-pets'));
    },
  };
  const productsCard: QuickActionCardConfig = {
    title: 'Better gear for your pet',
    subtitle: "Discover curated toys and gear tailored to your pet's unique needs and lifestyle",
    background: '#fff',
    buttonText: 'Explore Products',
    buttonColor: '#3700b3',
    image: '/hub/checklist/hub_toys_cta.png',
    imageHeight: QUICK_ACTION_IMAGE_HEIGHT,
    onClick: () => {
      mixpanel.track('explore_products_click', undefined, () => navigate('/hub/products'));
    },
  };
  const shopCard: QuickActionCardConfig = {
    title: 'Turn your pet into a legend',
    subtitle: "Upload a photo and we'll turn your pet into a vintage tee",
    background: '#fff',
    buttonText: 'Create Your Legend',
    buttonColor: '#3700b3',
    image: '/hub/hub_shop_cta.png',
    imageHeight: QUICK_ACTION_IMAGE_HEIGHT,
    onClick: () => {
      mixpanel.track('go_to_store_click', { link: shopUrl }, () => {
        window.open(shopUrl, '_blank');
      });
    },
  };
  return hasMedicalRecords(petParent)
    ? [membershipCard, recordsCard, productsCard, shopCard]
    : [membershipCard, productsCard, shopCard];
};
